import React, { useState, useCallback } from 'react';
import { X, Loader2, Search } from 'lucide-react';
import { useMarkets } from '../../hooks/useMarkets';
import { useSearches } from '../../hooks/useSearches';
import { Market } from '../../types/market';
import { useDebounce } from '../../hooks/useDebounce';

interface SearchFormProps {
  market: Market;
  onClose: () => void;
}

export const SearchForm: React.FC<SearchFormProps> = ({ market, onClose }) => {
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { createMarketSearch } = useMarkets();
  const { searches, isLoading, isFetching } = useSearches({
    page,
    pageSize: 5,
    searchTerm: debouncedSearchTerm
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) {
        throw new Error('Search query is required');
      }

      await createMarketSearch({
        marketId: market.id,
        query: trimmedQuery
      });
      
      onClose();
    } catch (err) {
      console.error('Error submitting search:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleExistingSearchSelect = async (searchQuery: string) => {
    setLoading(true);
    try {
      await createMarketSearch({
        marketId: market.id,
        query: searchQuery
      });
      onClose();
    } catch (err) {
      console.error('Error adding existing search:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
    }
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page when search term changes
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !isFetching) {
      setPage(prev => prev + 1);
    }
  }, [isFetching]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        
        <div className="inline-block w-full max-w-md my-8 text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
          <div className="relative p-6">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Add Search to {market.name}
            </h3>

            <div className="space-y-6">
              {/* Search existing queries */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Existing Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search existing queries..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div 
                  className="mt-2 max-h-48 overflow-y-auto"
                  onScroll={handleScroll}
                >
                  {isLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : searches.length === 0 ? (
                    <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                      No matches found
                    </p>
                  ) : (
                    searches.map((search) => (
                      <button
                        key={search.id}
                        onClick={() => handleExistingSearchSelect(search.query)}
                        className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <p className="text-sm text-gray-900 dark:text-white">{search.query}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <span>{search.searches.toLocaleString()} searches</span>
                          <span>•</span>
                          <span>{search.likes} likes</span>
                        </div>
                      </button>
                    ))
                  )}
                  {isFetching && !isLoading && (
                    <div className="flex justify-center py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    </div>
                  )}
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">or create new</span>
                </div>
              </div>

              {/* Create new search */}
              <form onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="query" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Search Query
                  </label>
                  <textarea
                    id="query"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    rows={3}
                    className={`mt-1 block w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white ${
                      error ? 'border-red-300 bg-red-50 dark:border-red-500 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                    }`}
                    placeholder="Enter a new search query..."
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="mt-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-start space-x-2">
                    <span className="flex-shrink-0">!</span>
                    <span>{error}</span>
                  </div>
                )}

                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !query.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[100px]"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Add Search'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};